import { useEffect, useMemo, useRef, useState } from 'react'
import { useEditor } from '../store/context'
import { usePreviewStore } from '../store/preview-store'
import { useEditorMarkdown } from '../utils/tiptap'
import iframeCss from './iframe.css?inline'

function buildIframeSrcdoc(): string {
  const stylesheets = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  ).map(l => `<link rel="stylesheet" href="${l.href}">`)

  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[src]'),
  )
    .map(s => s.src)
    .filter(src => src && !src.includes('tiptap.bundle'))
    .map(src => `<script src="${src}"><\/script>`)

  return [
    '<!DOCTYPE html><html><head><meta charset="utf-8">',
    ...stylesheets,
    `<style>${iframeCss}</style>`,
    '</head><body class="wiki">',
    ...scripts,
    '</body></html>',
  ].join('\n')
}

export function PreviewPane() {
  const { view, store } = useEditor()

  const markdown = useEditorMarkdown(store.rawEditor)
  const markdownRef = useRef(markdown)
  markdownRef.current = markdown

  const previewStore = usePreviewStore(store.adapter)

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeReady, setIframeReady] = useState(false)

  const srcdoc = useMemo(() => buildIframeSrcdoc(), [])

  useEffect(() => {
    if (markdown === undefined)
      return
    previewStore.scheduleRefresh(markdown)
    // eslint-disable-next-line react/exhaustive-deps
  }, [markdown])

  useEffect(() => {
    const md = markdownRef.current
    if (view.viewMode !== 'editor' || md === undefined)
      return
    previewStore.refresh(md)
    // eslint-disable-next-line react/exhaustive-deps
  }, [view.viewMode])

  useEffect(() => {
    if (!iframeReady)
      return
    const html = previewStore.html.latest
    const doc = iframeRef.current?.contentDocument
    if (!doc)
      return
    doc.body.innerHTML = html ? `<div class="wiki">${html}</div>` : ''
  }, [iframeReady, previewStore.html])

  function handleLoad() {
    setIframeReady(true)
    const html = previewStore.html.latest
    const doc = iframeRef.current?.contentDocument
    if (html && doc) {
      doc.body.innerHTML = `<div class="wiki">${html}</div>`
    }
  }

  if (view.viewMode === 'editor')
    return null

  return (
    <div className="tiptap-preview-pane">
      {previewStore.html.loading && <div className="tiptap-preview-loading-bar" />}
      <iframe
        ref={iframeRef}
        title="Preview"
        srcDoc={srcdoc}
        style={{ width: '100%', flex: '1', minHeight: '0', border: 'none', display: 'block' }}
        onLoad={handleLoad}
      />
    </div>
  )
}
